import React, {
  useState,
  ChangeEvent,
  FormEvent,
  useMemo,
  useRef,
  useEffect,
} from 'react';
import {useIntl, defineMessages, FormattedMessage} from 'react-intl';
import {
  CurrencyField,
  DateField,
  TextField,
  view,
  FormFieldGroup,
  Divider,
  InlineFeedback,
  Link,
} from '@sail/ui';
import HCaptcha from '@hcaptcha/react-hcaptcha';
import {FormStep} from './FormStep';
import {SubmitButton} from './SubmitButton';
import {validateAmount, validateIban, validatePaymentDate} from './validate';

export const messages = defineMessages({
  amountLabel: {
    id: 'ideal_inquiry.amount_label',
    defaultMessage: 'Payment amount',
  },
  paymentDateLabel: {
    id: 'ideal_inquiry.payment_date_label',
    defaultMessage: 'Payment date',
  },
  ibanLabel: {
    id: 'ideal_inquiry.iban_label',
    defaultMessage: 'Your IBAN',
  },
  ibanPlaceholder: {
    id: 'ideal_inquiry.iban_placeholder',
    defaultMessage: 'NL00BANK0123456789',
  },
  searchTitle: {
    id: 'ideal_inquiry.search_title',
    defaultMessage: 'iDEAL payment lookup',
  },
  findPaymentButton: {
    id: 'ideal_inquiry.find_payment_button',
    defaultMessage: 'Find payment',
  },
  errorLoadingHcaptcha: {
    id: 'ideal_inquiry.error_loading_hcaptcha',
    defaultMessage: 'Error loading hCaptcha. Please try again later.',
  },
  legalText: {
    id: 'ideal_inquiry.legal_text',
    defaultMessage:
      "By submitting this form, you agree to Stripe's <privacyPolicyLink>Privacy Policy</privacyPolicyLink>",
  },
});

export interface SearchFormData {
  amount: number | null;
  payment_date: string;
  iban: string;
  hcaptcha_token: string | null;
  hcaptcha_ekey: string | null;
  origin: string;
}

interface SearchFormProps {
  initialAmount: number | null;
  initialPaymentDate?: string;
  initialIban?: string;
  handleSubmit: (formData: SearchFormData) => void;
  isPending?: boolean;
  isDisabled?: boolean;
  submissionError?: string | null;
  clearErrorOnInteraction: () => void;
  hcaptchaSitekey?: string;
  isFormComplete: boolean;
}

export function SearchPaymentForm({
  initialAmount = null,
  initialPaymentDate = '',
  initialIban = '',
  handleSubmit,
  isPending = false,
  isDisabled = false,
  submissionError = null,
  clearErrorOnInteraction,
  hcaptchaSitekey,
  isFormComplete,
}: SearchFormProps) {
  const intl = useIntl();

  const [formData, setFormData] = useState<SearchFormData>({
    amount: initialAmount,
    payment_date: initialPaymentDate,
    iban: initialIban,
    hcaptcha_token: null,
    hcaptcha_ekey: null,
    origin: window.location.origin,
  });

  const [formErrors, setFormErrors] = useState<
    Partial<Record<keyof SearchFormData, string>>
  >({});

  const hcaptchaRef = useRef<HCaptcha | null>(null);

  useEffect(() => {
    if (submissionError && hcaptchaRef.current) {
      hcaptchaRef.current.resetCaptcha();
    }

    // Reset hCaptcha state
    setFormData((prev) => ({
      ...prev,
      hcaptcha_token: null,
      hcaptcha_ekey: null,
    }));
  }, [submissionError]);

  // Validate form fields
  const validateFields = (): Partial<Record<keyof SearchFormData, string>> => {
    return {
      amount: validateAmount(formData.amount, intl),
      payment_date: validatePaymentDate(formData.payment_date, intl),
      iban: validateIban(formData.iban, intl),
    };
  };

  const isButtonEnabled = useMemo(() => {
    return (
      formData.amount !== null &&
      formData.payment_date !== '' &&
      // '--' is the default state for DateField when no date is specified
      formData.payment_date !== '--' &&
      formData.iban !== null &&
      formData.iban !== '' &&
      formData.hcaptcha_token !== null &&
      formData.hcaptcha_ekey !== null
    );
  }, [formData]);

  const handleTextChange = (
    event: ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    clearErrorOnInteraction();
    const {name, value} = event.target;
    setFormData((prev) => ({...prev, [name]: value}));
    if (name in formErrors) {
      setFormErrors((prevErrors) => ({...prevErrors, [name]: undefined}));
    }
  };

  const handleDateChange = (value: string) => {
    clearErrorOnInteraction();
    setFormData((prev) => ({...prev, payment_date: value}));
    if ('payment_date' in formErrors) {
      setFormErrors((prevErrors) => ({
        ...prevErrors,
        payment_date: undefined,
      }));
    }
  };

  const handleAmountChange = (value: number | null) => {
    clearErrorOnInteraction();
    setFormData((prev) => ({
      ...prev,
      amount: value,
    }));
    if ('amount' in formErrors) {
      setFormErrors((prevErrors) => ({
        ...prevErrors,
        amount: undefined,
      }));
    }
  };

  const handleHcaptchaChange = (token: string, ekey: string) => {
    setFormData((prev) => ({
      ...prev,
      hcaptcha_token: token,
      hcaptcha_ekey: ekey,
    }));
  };

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const errors = validateFields();
    const hasErrors = Object.values(errors).some((err) => err !== undefined);

    if (hasErrors) {
      setFormErrors(errors);
      return;
    }

    handleSubmit(formData);
  };

  const hCaptchaSection = () => {
    return (
      <>
        {hcaptchaSitekey !== null ? (
          <view.div css={{stack: 'y', alignX: 'stretch', gap: 'medium'}}>
            <Divider />

            <HCaptcha
              ref={hcaptchaRef}
              sitekey={hcaptchaSitekey!}
              onVerify={handleHcaptchaChange}
            />
          </view.div>
        ) : (
          <InlineFeedback type="error">
            {intl.formatMessage(messages.errorLoadingHcaptcha)}
          </InlineFeedback>
        )}
      </>
    );
  };

  return (
    <>
      <script src="https://js.hcaptcha.com/1/api.js" />
      <FormStep isFormComplete={isFormComplete}>
        <view.form
          css={{stack: 'y', gap: 'medium', position: 'relative'}}
          onSubmit={onSubmit}
        >
          <view.div css={{stack: 'y', alignX: 'stretch', gap: 'small'}}>
            <view.span css={{font: 'label.large.emphasized'}}>
              {intl.formatMessage(messages.searchTitle)}
            </view.span>
          </view.div>

          <FormFieldGroup layout="vertical" error={submissionError}>
            <view.div
              css={{
                stack: 'y',
                alignX: 'stretch',
                gap: 'medium',
              }}
            >
              <TextField
                data-testid="iban-input"
                label={intl.formatMessage(messages.ibanLabel)}
                name="iban"
                placeholder={intl.formatMessage(messages.ibanPlaceholder)}
                required
                value={formData.iban}
                onChange={handleTextChange}
                error={formErrors.iban}
                disabled={isDisabled || isFormComplete}
                size="medium"
              />

              <CurrencyField
                data-testid="amount-input"
                currencyAllowlist={['EUR']}
                label={intl.formatMessage(messages.amountLabel)}
                name="amount"
                value={formData.amount}
                onChange={handleAmountChange}
                error={formErrors.amount}
                disabled={isDisabled || isFormComplete}
                placeholderValue={1999}
                size="medium"
              />

              <DateField
                data-testid="payment-date-input"
                format="DMY"
                label={intl.formatMessage(messages.paymentDateLabel)}
                onChange={handleDateChange}
                error={formErrors.payment_date}
                disabled={isDisabled || isFormComplete}
                value={formData.payment_date}
                size="medium"
              />
              {!isFormComplete ? hCaptchaSection() : null}
            </view.div>
          </FormFieldGroup>
          {!isFormComplete ? (
            <view.div
              css={{
                stack: 'y',
                alignX: 'stretch',
                gap: 'small',
              }}
            >
              <view.span css={{font: 'label.small', color: 'subdued'}}>
                <FormattedMessage
                  {...messages.legalText}
                  values={{
                    privacyPolicyLink: (chunks) => (
                      <Link
                        type="primary"
                        href="https://stripe.com/privacy"
                        target="_blank"
                        rel="noopener noreferrer"
                        css={{fontWeight: 'inherit'}}
                      >
                        {chunks}
                      </Link>
                    ),
                  }}
                />
              </view.span>
              <SubmitButton
                data-testid="find-payment-button"
                behavior="submit"
                pending={isPending}
                disabled={!isButtonEnabled || isDisabled}
                type="primary"
                size="medium"
              >
                {intl.formatMessage(messages.findPaymentButton)}
              </SubmitButton>
            </view.div>
          ) : null}
        </view.form>
      </FormStep>
    </>
  );
}
